// Server-side in-memory tracking store

export interface PageVisit {
  slide: number;
  slideName: string;
  enteredAt: string;
  timeSpentSeconds: number;
}

export interface ClickEvent {
  action: string;
  label: string;
  slide: number;
  timestamp: string;
}

export type LeadTemperature = 'ice_cold' | 'cold' | 'warm' | 'hot' | 'extremely_hot';

export type BehaviorTag =
  | 'just_browsing' |'engaged_viewer' |'path_explorer' |'package_browser' |'order_initiator' |'order_abandoner' |'order_submitted' |'webinar_registered' |'high_intent' |'business_track' |'product_track';

export interface VisitorSession {
  visitorId: string;
  leadId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  startedAt: string;
  lastSeenAt: string;
  totalTimeSeconds: number;
  maxSlideReached: number;
  pageVisits: PageVisit[];
  clickEvents: ClickEvent[];
  tags: BehaviorTag[];
  temperature: LeadTemperature;
  score: number;
  path: 'A' | 'B' | null;
  orderStarted: boolean;
  orderSubmitted: boolean;
}

declare global {
  // eslint-disable-next-line no-var
  var __trackingStore: Map<string, VisitorSession> | undefined;
}

if (!global.__trackingStore) {
  global.__trackingStore = new Map();
}

export function getSession(visitorId: string): VisitorSession | undefined {
  return global.__trackingStore?.get(visitorId);
}

export function getAllSessions(): VisitorSession[] {
  return Array.from(global.__trackingStore?.values() ?? []).sort(
    (a, b) => new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime()
  );
}

export function upsertSession(session: VisitorSession): void {
  if (!global.__trackingStore) global.__trackingStore = new Map();
  global.__trackingStore.set(session.visitorId, session);
}

// ─── Scoring Engine ────────────────────────────────────────────────────────────

const SLIDE_NAMES: Record<number, string> = {
  1: 'Lead Capture',
  2: 'Hook',
  3: 'Science',
  4: 'Product',
  5: 'Decision',
  6: 'Path Detail',
  7: 'Packages / Order',
  8: 'Order Form',
  9: 'Order / Webinar',
  10: 'Final',
};

export function getSlideName(slide: number): string {
  return SLIDE_NAMES[slide] ?? `Slide ${slide}`;
}

export function computeScore(session: Partial<VisitorSession>): number {
  let score = 0;

  // Slide depth
  const maxSlide = session.maxSlideReached ?? 1;
  score += Math.min(maxSlide * 8, 60); // up to 60 pts for depth

  // Time on site (1 pt per 30 seconds, max 20)
  const totalTime = session.totalTimeSeconds ?? 0;
  score += Math.min(Math.floor(totalTime / 30), 20);

  // Path selected
  if (session.path) score += 10;

  // Order page reached
  if ((session.maxSlideReached ?? 0) >= 7) score += 15;

  // Order started (clicked into form)
  if (session.orderStarted) score += 20;

  // Order submitted
  if (session.orderSubmitted) score += 40;

  // Click engagement
  const clicks = session.clickEvents?.length ?? 0;
  score += Math.min(clicks * 2, 10);

  return Math.min(score, 100);
}

export function computeTemperature(score: number): LeadTemperature {
  if (score >= 85) return 'extremely_hot';
  if (score >= 65) return 'hot';
  if (score >= 40) return 'warm';
  if (score >= 20) return 'cold';
  return 'ice_cold';
}

export function computeTags(session: Partial<VisitorSession>): BehaviorTag[] {
  const tags: BehaviorTag[] = [];
  const maxSlide = session.maxSlideReached ?? 1;
  const totalTime = session.totalTimeSeconds ?? 0;

  if (maxSlide <= 2 && totalTime < 60) tags.push('just_browsing');
  if (totalTime >= 120 && maxSlide >= 3) tags.push('engaged_viewer');
  if (maxSlide >= 5) tags.push('path_explorer');
  if (session.path === 'A') tags.push('business_track');
  if (session.path === 'B') tags.push('product_track');
  if (maxSlide >= 7) tags.push('package_browser');
  if (maxSlide >= 7 && !session.orderSubmitted) tags.push('high_intent');
  if (session.orderStarted && !session.orderSubmitted) tags.push('order_abandoner');
  if (session.orderStarted) tags.push('order_initiator');
  if (session.orderSubmitted) {
    tags.push('order_submitted');
    // Remove abandoner if they submitted
    const idx = tags.indexOf('order_abandoner');
    if (idx > -1) tags.splice(idx, 1);
  }

  return [...new Set(tags)];
}

export const temperatureConfig: Record<LeadTemperature, { label: string; color: string; bg: string; border: string; emoji: string }> = {
  ice_cold:     { label: 'Ice Cold',       color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.2)',  emoji: '🧊' },
  cold:         { label: 'Cold',           color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',   border: 'rgba(96,165,250,0.2)',   emoji: '❄️' },
  warm:         { label: 'Warm',           color: '#fb923c', bg: 'rgba(251,146,60,0.1)',   border: 'rgba(251,146,60,0.2)',   emoji: '🌡️' },
  hot:          { label: 'Hot',            color: '#f87171', bg: 'rgba(248,113,113,0.1)',  border: 'rgba(248,113,113,0.2)',  emoji: '🔥' },
  extremely_hot:{ label: 'Extremely Hot',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)',   emoji: '🚨' },
};

export const tagConfig: Record<BehaviorTag, { label: string; color: string; bg: string }> = {
  just_browsing:      { label: 'Just Browsing',      color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
  engaged_viewer:     { label: 'Engaged Viewer',     color: '#60a5fa', bg: 'rgba(96,165,250,0.1)'  },
  path_explorer:      { label: 'Path Explorer',      color: '#a78bfa', bg: 'rgba(167,139,250,0.1)' },
  package_browser:    { label: 'Package Browser',    color: '#fb923c', bg: 'rgba(251,146,60,0.1)'  },
  order_initiator:    { label: 'Order Initiator',    color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
  order_abandoner:    { label: 'Order Abandoner',    color: '#fbbf24', bg: 'rgba(251,191,36,0.1)'  },
  order_submitted:    { label: 'Order Submitted',    color: '#4ade80', bg: 'rgba(74,222,128,0.1)'  },
  webinar_registered: { label: 'Webinar Registered', color: '#34d399', bg: 'rgba(52,211,153,0.1)'  },
  high_intent:        { label: 'High Intent',        color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
  business_track:     { label: 'Business Track',     color: '#00a8ff', bg: 'rgba(0,168,255,0.1)'   },
  product_track:      { label: 'Product Track',      color: '#818cf8', bg: 'rgba(129,140,248,0.1)' },
};
